import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { JitsiRoom } from "./JitsiRoom";
import { SearchableSelect } from "./SearchableSelect";
import { fmtAppt, fmtApptTime, apptStartMs, localTz } from "../lib/datetime";
import {
  Stethoscope, Plus, Trash2, Video, Phone, MessageSquare, CalendarPlus,
  CheckCircle2, Bell, XCircle, Loader2, Activity, Clock, AlertTriangle, Pencil, Check, Mic, MapPin,
  CalendarClock, Search, X, Link2, MessageCircle, Languages,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : "");

// Subtle chime via Web Audio (no asset needed).
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
    o.start(); o.stop(ctx.currentTime + 0.42);
    setTimeout(() => ctx.close(), 700);
  } catch {}
}

function notifyDesktop(title, body) {
  try {
    if ("Notification" in window && Notification.permission === "granted") new Notification(title, { body });
  } catch {}
}

const inp = "w-full border border-line bg-white rounded-md px-3 py-2 text-sm outline-none focus:border-clinical";
const MODE_ICON = { video: Video, audio: Phone, text: MessageSquare, walk_in: MapPin };
const STATUS_STYLE = { scheduled: "text-clinical bg-clinical/10", completed: "text-calm bg-calm/10", cancelled: "text-critical bg-critical/10" };

// Staff-side strip: live & upcoming consultations with one-click join.
export function LiveConsultations() {
  const [list, setList] = useState([]);
  const [join, setJoin] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const load = useCallback(async () => { try { setList((await api.get("/appointments")).data); } catch {} }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { setNowTs(Date.now()); load(); }, 30000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Chime + desktop notification when a patient newly enters the waiting room.
  const notifiedRef = useRef(new Set());
  useEffect(() => {
    const waitingIds = list.filter((a) => a.patient_waiting).map((a) => a.id);
    waitingIds.forEach((id) => {
      if (!notifiedRef.current.has(id)) {
        notifiedRef.current.add(id);
        const appt = list.find((a) => a.id === id);
        playChime();
        toast(`${appt?.patient_name} is waiting`, {
          description: `For ${appt?.specialist_name} · ${appt?.mode}`,
          action: { label: "Join", onClick: () => setJoin(id) },
          duration: 10000,
        });
        notifyDesktop("Patient is waiting", `${appt?.patient_name} is waiting for ${appt?.specialist_name}`);
      }
    });
    notifiedRef.current.forEach((id) => { if (!waitingIds.includes(id)) notifiedRef.current.delete(id); });
  }, [list]);

  const relevant = list
    .map((a) => ({ ...a, _start: apptStartMs(a), _dur: (a.duration || 30) * 60000 }))
    .filter((a) => a.status === "scheduled" && a._start && nowTs <= a._start + a._dur && nowTs >= a._start - 60 * 60000)
    .sort((x, y) => x._start - y._start);

  if (relevant.length === 0) return null;

  return (
    <div data-testid="live-consultations" className="mb-8 bg-white border border-line rounded-xl p-5">
      <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2 mb-4">
        <Activity className="w-5 h-5 text-critical" /> Live &amp; upcoming consultations
      </h2>
      <div className="grid gap-2">
        {relevant.map((a) => {
          const mins = Math.round((a._start - nowTs) / 60000);
          const live = nowTs >= a._start;
          const MI = MODE_ICON[a.mode] || Video;
          const wsince = a.waiting_since ? new Date(a.waiting_since).getTime() : null;
          const waitMins = wsince ? Math.max(0, Math.round((nowTs - wsince) / 60000)) : 0;
          const escalated = a.patient_waiting && waitMins >= 3;
          return (
            <div key={a.id} data-testid="live-consultation-row" className={`flex flex-wrap items-center justify-between gap-3 rounded-lg px-4 py-3 border ${escalated ? "bg-critical/10 border-critical" : live ? "bg-critical/5 border-critical/30" : "border-line"}`}>
              <div className="flex items-center gap-3">
                {live ? (
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-critical/70 opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-critical" />
                  </span>
                ) : <Clock className="w-4 h-4 text-muted2" />}
                <div>
                  <p className="font-heading font-bold text-ink flex items-center gap-2 flex-wrap">
                    {a.specialist_name}
                    <span className="text-xs font-bold uppercase px-2 py-0.5 rounded inline-flex items-center gap-1 text-clinical bg-clinical/10"><MI className="w-3 h-3" /> {a.mode}</span>
                    {a.patient_waiting && (
                      <span data-testid="waiting-badge" className={`text-xs font-bold uppercase px-2 py-0.5 rounded animate-pulse ${escalated ? "bg-critical text-white" : "bg-alert/20 text-alert"}`}>
                        ● Waiting {waitMins}m
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-muted2">{a.patient_name} ({a.public_code}) · {live ? "in progress" : `starts in ${mins} min`} · {fmtApptTime(a)}</p>
                </div>
              </div>
              <button onClick={() => setJoin(a.id)} data-testid="live-join-btn" className={`inline-flex items-center gap-1.5 font-semibold rounded-md px-4 py-2 text-sm text-white transition-colors ${live ? "bg-critical hover:bg-critical/90" : "bg-clinical hover:bg-clinicaldark"}`}>
                <Video className="w-4 h-4" /> {live ? "Join now" : "Join"}
              </button>
            </div>
          );
        })}
      </div>
      {join && <JitsiRoom appointmentId={join} onClose={() => { setJoin(null); load(); }} />}
    </div>
  );
}

export function AdminSpecialists() {
  const [list, setList] = useState([]);
  const blank = { name: "", title: "", specialty: "", active: true };
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const load = useCallback(async () => { try { setList((await api.get("/specialists")).data); } catch (e) { toast.error(apiErr(e)); } }, []);
  useEffect(() => { load(); }, [load]);
  const reset = () => { setForm(blank); setEditingId(null); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      if (editingId) { await api.put(`/specialists/${editingId}`, form); toast.success("Updated"); }
      else { await api.post("/specialists", form); toast.success("Added"); }
      reset(); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const edit = (s) => { setEditingId(s.id); setForm({ name: s.name || "", title: s.title || "", specialty: s.specialty || "", active: s.active !== false }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => { try { await api.delete(`/specialists/${id}`); toast.success("Removed"); if (editingId === id) reset(); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div>
      <h1 className="font-heading font-bold text-3xl tracking-tight text-ink mb-6 flex items-center gap-2"><Stethoscope className="w-7 h-7 text-clinical" /> Clinical Specialists</h1>
      <div className="bg-white border border-line rounded-md p-4 mb-6 grid sm:grid-cols-3 gap-3" data-testid="specialist-form">
        <input className={inp} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="specialist-name-input" />
        <input className={inp} placeholder="Title (e.g. Doctor)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <input className={inp} placeholder="Specialty" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} />
        <div className="sm:col-span-3 flex gap-2">
          <button onClick={save} data-testid="specialist-save-btn" className="inline-flex items-center justify-center gap-1.5 bg-clinical text-white rounded-md px-4 py-2 text-sm font-semibold flex-1">{editingId ? <><Check className="w-4 h-4" /> Update specialist</> : <><Plus className="w-4 h-4" /> Add specialist</>}</button>
          {editingId && <button onClick={reset} data-testid="specialist-cancel-btn" className="inline-flex items-center justify-center gap-1.5 border border-line text-ink rounded-md px-4 py-2 text-sm font-semibold">Cancel</button>}
        </div>
      </div>
      <div className="grid gap-3">
        {list.map((s) => (
          <div key={s.id} data-testid="specialist-row" className={`bg-white border rounded-md p-4 flex items-center justify-between ${editingId === s.id ? "border-clinical ring-1 ring-clinical/30" : "border-line"}`}>
            <div><span className="font-heading font-bold text-ink">{s.title} {s.name}</span><p className="text-sm text-muted2">{s.specialty}</p></div>
            <div className="flex items-center gap-2">
              <button data-testid="specialist-edit-btn" onClick={() => edit(s)} className="text-clinical"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(s.id)} className="text-critical"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const ROLE_LABEL = { system_admin: "System Admin", super_admin: "Super Admin", admin: "Admin", clinical_specialist: "Clinician" };

// Multi-select dropdown of extra clinicians/admin staff — searches the server as you type.
function ParticipantPicker({ selectedItems, onToggle }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/appointment-participants", { params: { q: query.trim(), limit: 20 } }); if (active) setResults(data); }
      catch { if (active) setResults([]); }
      finally { if (active) setLoading(false); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [open, query]);
  const selectedIds = selectedItems.map((s) => s.id);
  return (
    <div className="lg:col-span-3" data-testid="participant-picker" ref={ref}>
      <p className="text-xs uppercase tracking-wider font-bold text-muted2 mb-2">Add extra clinicians &amp; admin staff (optional) — they can also join this consultation</p>
      <div className="relative">
        <div
          role="button"
          tabIndex={0}
          data-testid="participant-select-trigger"
          onClick={() => setOpen((o) => !o)}
          className="w-full min-h-[42px] border border-line bg-white rounded-md px-3 py-2 text-sm cursor-pointer flex flex-wrap items-center gap-1.5"
        >
          {selectedItems.length === 0 && <span className="text-muted2">Search &amp; select clinicians or admin staff…</span>}
          {selectedItems.map((u) => (
            <span key={u.id} data-testid="participant-chip" className="inline-flex items-center gap-1 text-xs font-semibold bg-clinical/10 text-clinical rounded-full pl-2.5 pr-1 py-0.5">
              {u.name}
              <button type="button" data-testid="participant-chip-remove" onClick={(e) => { e.stopPropagation(); onToggle(u); }} className="rounded-full hover:bg-clinical/20 p-0.5">
                <XCircle className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
        {open && (
          <div data-testid="participant-dropdown" className="absolute z-30 mt-1 w-full bg-white border border-line rounded-md shadow-lg">
            <div className="p-2 border-b border-line flex items-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 text-muted2 shrink-0 animate-spin" /> : null}
              <input
                autoFocus
                data-testid="participant-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name…"
                className="w-full border border-line rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-clinical"
              />
            </div>
            <div className="max-h-56 overflow-y-auto py-1">
              {results.length === 0 ? (
                <p className="text-sm text-muted2 px-3 py-2">{loading ? "Searching…" : `No staff match${query ? ` “${query}”` : ""}.`}</p>
              ) : results.map((u) => {
                const active = selectedIds.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    data-testid="participant-option"
                    onClick={() => onToggle(u)}
                    className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2 text-sm transition-colors ${active ? "bg-clinical/10 text-clinical font-semibold" : "text-ink hover:bg-line/50"}`}
                  >
                    <span>{u.name} <span className="text-muted2 font-normal">· {ROLE_LABEL[u.role] || u.role}</span></span>
                    {active && <CheckCircle2 className="w-4 h-4 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Clickable published availability slots that fill the scheduler's date & time.
function AvailabilityPicker({ slots, date, time, onPick }) {
  return (
    <div className="lg:col-span-3" data-testid="availability-picker">
      <p className="text-xs uppercase tracking-wider font-bold text-muted2 mb-2">Published availability — click a slot to fill date &amp; time</p>
      {slots.length === 0 ? (
        <p className="text-sm text-muted2">This clinician has not published any upcoming availability.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {slots.map((s) => {
            const active = date === s.date && time === s.start_time;
            return (
              <button
                key={s.id}
                type="button"
                data-testid="availability-slot-btn"
                onClick={() => onPick(s.date, s.start_time)}
                className={`text-xs font-semibold rounded-md px-3 py-1.5 border transition-colors ${active ? "bg-clinical text-white border-clinical" : "border-clinical/30 text-clinical hover:bg-clinical/5"}`}
              >
                {s.date} · {s.start_time}–{s.end_time}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Optional interpreter assignment: searchable verified interpreters, filtered by language,
// with an availability hint and a hard double-booking guard (conflicted ones are disabled).
function InterpreterPicker({ value, onChange, language, date, time, duration, excludeAid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get("/interpreters", { params: { language: language || "", date: date || "", time: time || "", duration: Number(duration) || 30, exclude_aid: excludeAid || "" } })
      .then(({ data }) => { if (active) setItems(data); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [language, date, time, duration, excludeAid]);
  const canFilterAvail = Boolean(date && time);
  const shown = (onlyAvailable && canFilterAvail) ? items.filter((i) => i.available === true) : items;
  const selected = items.find((i) => i.id === value);
  return (
    <div className="lg:col-span-3 border border-line rounded-md p-3 bg-sand/40" data-testid="interpreter-picker">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <p className="text-xs uppercase tracking-wider font-bold text-muted2">Assign an interpreter (optional){language ? ` — for ${language}` : ""}</p>
        <div className="flex items-center gap-3">
          {canFilterAvail && (
            <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted2 cursor-pointer" data-testid="interpreter-only-available">
              <input type="checkbox" checked={onlyAvailable} onChange={(e) => setOnlyAvailable(e.target.checked)} className="w-3.5 h-3.5 accent-clinical" />
              Only free at this time
            </label>
          )}
          {value && <button type="button" data-testid="interpreter-clear-btn" onClick={() => onChange("")} className="text-xs font-semibold text-critical hover:underline">Clear</button>}
        </div>
      </div>
      {loading && <p className="text-sm text-muted2">Searching interpreters…</p>}
      {!loading && shown.length === 0 && <p className="text-sm text-muted2" data-testid="interpreter-none">{onlyAvailable && canFilterAvail ? "No interpreters marked free at this time." : `No verified interpreters${language ? ` for ${language}` : ""} found.`}</p>}
      <div className="flex flex-wrap gap-2">
        {shown.map((it) => {
          const active = it.id === value;
          const disabled = it.has_conflict && !active;
          return (
            <button key={it.id} type="button" data-testid="interpreter-option" disabled={disabled}
              onClick={() => onChange(active ? "" : it.id)}
              title={disabled ? `Already booked at ${it.conflict_when}` : ""}
              className={`text-left text-xs rounded-md px-3 py-2 border transition-colors ${active ? "bg-clinical text-white border-clinical" : disabled ? "border-line text-muted2 opacity-50 cursor-not-allowed" : "border-clinical/30 text-ink hover:bg-clinical/5"}`}>
              <span className="font-bold block">{it.name}</span>
              <span className={`block ${active ? "text-white/80" : "text-muted2"}`}>{(it.languages || []).join(", ") || "—"}</span>
              {it.has_conflict && <span className="block text-[11px] font-semibold text-alert" data-testid="interpreter-conflict">Booked {it.conflict_when}</span>}
              {!it.has_conflict && date && time && it.available === true && <span className="block text-[11px] font-semibold text-calm" data-testid="interpreter-available">✓ Available</span>}
              {!it.has_conflict && date && time && it.available === false && it.availability_known && <span className="block text-[11px] text-muted2">Outside stated availability</span>}
            </button>
          );
        })}
      </div>
      {selected && <p className="text-xs text-clinical font-semibold mt-2" data-testid="interpreter-selected">Interpreter: {selected.name}</p>}
    </div>
  );
}


// Per-row control to assign/change/remove the interpreter after booking.
function InterpreterRowControl({ appt, onReassign }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="w-full mt-2" data-testid="appt-interpreter-control">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="appt-interpreter-toggle"
        className="inline-flex items-center gap-1 text-xs font-semibold text-clinical border border-clinical/30 rounded-md px-2.5 py-1 hover:bg-clinical/5">
        <Languages className="w-3.5 h-3.5" /> {appt.interpreter_name ? "Change interpreter" : "Assign interpreter"}
      </button>
      {open && (
        <div className="mt-2 grid grid-cols-1">
          <InterpreterPicker
            value={appt.interpreter_id || ""}
            language={appt.language || ""}
            date={appt.date} time={appt.time} duration={appt.duration}
            excludeAid={appt.id}
            onChange={(v) => { onReassign(appt.id, v); setOpen(false); }}
          />
        </div>
      )}
    </div>
  );
}

// Clinic filter + search + the list of appointment rows with their per-status actions.
function AppointmentList({ items, total, clinics, clinicFilter, onClinicFilter, search, onSearch, onJoin, onRemind, onComplete, onCancel, onRemove, onReschedule, onWhatsapp, onReassignInterpreter, isSysAdmin, selected, onToggleSelect, onBulkDelete }) {
  const clinicName = (id) => clinics.find((c) => c.id === id)?.name || "";
  const needsAttention = items.filter((a) => a.interpreter_inactive);
  return (
    <>
      {needsAttention.length > 0 && (
        <div className="flex items-start gap-2 mb-3 bg-critical/10 border border-critical/30 rounded-md px-3 py-2.5" data-testid="interpreter-attention-banner">
          <AlertTriangle className="w-4 h-4 text-critical shrink-0 mt-0.5" />
          <span className="text-sm text-ink">
            <b>{needsAttention.length} upcoming consultation{needsAttention.length === 1 ? "" : "s"}</b> {needsAttention.length === 1 ? "has" : "have"} a deactivated interpreter assigned. Use “Change interpreter” on the flagged {needsAttention.length === 1 ? "row" : "rows"} below to reassign.
          </span>
        </div>
      )}
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="appt-clinic-filter-row">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-muted2 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="appt-search-input"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search by patient, username, ID, date or clinician…"
            className={`${inp} pl-9`}
          />
          {search && <button data-testid="appt-search-clear" onClick={() => onSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted2 hover:text-ink"><X className="w-4 h-4" /></button>}
        </div>
        <select className={`${inp} max-w-xs`} value={clinicFilter} onChange={(e) => onClinicFilter(e.target.value)} data-testid="appt-clinic-filter">
          <option value="all">All clinics</option>
          {clinics.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <span className="text-xs text-muted2">{items.length} appointment(s)</span>
      </div>
      {isSysAdmin && selected.length > 0 && (
        <div className="flex items-center gap-3 mb-3 bg-critical/5 border border-critical/20 rounded-md px-3 py-2" data-testid="appt-bulk-bar">
          <span className="text-sm font-semibold text-critical">{selected.length} selected</span>
          <button onClick={onBulkDelete} data-testid="appt-bulk-delete-btn" className="inline-flex items-center gap-1 bg-critical text-white text-sm font-semibold rounded-md px-3 py-1.5"><Trash2 className="w-4 h-4" /> Delete selected</button>
        </div>
      )}
      <div className="grid gap-3" data-testid="appointment-list">
        {items.map((a) => {
          const MI = MODE_ICON[a.mode] || Video;
          const checked = selected.includes(a.id);
          return (
            <div key={a.id} data-testid="appointment-row" className="bg-white border border-line rounded-md p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                {isSysAdmin && (
                  <input type="checkbox" checked={checked} onChange={() => onToggleSelect(a.id)} data-testid="appt-select-checkbox" className="mt-1 w-4 h-4 rounded border-line accent-critical cursor-pointer" />
                )}
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-bold text-ink">{a.specialist_name}</span>
                    <span className="text-xs font-bold uppercase px-2 py-0.5 rounded inline-flex items-center gap-1 text-clinical bg-clinical/10"><MI className="w-3 h-3" /> {a.mode === "walk_in" ? "Walk-in" : a.mode}</span>
                    <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${STATUS_STYLE[a.status]}`}>{a.status}</span>
                    {a.clinic_id && clinicName(a.clinic_id) ? (
                      <span data-testid="appt-clinic-badge" className="text-xs font-semibold px-2 py-0.5 rounded bg-calm/10 text-calm">{clinicName(a.clinic_id)}</span>
                    ) : (
                      <span data-testid="appt-clinic-badge" className="text-xs font-semibold px-2 py-0.5 rounded bg-clinical/10 text-clinical">Virtual Consultation</span>
                    )}
                    {a.language && <span data-testid="appt-language-badge" className="text-xs font-semibold px-2 py-0.5 rounded bg-sand text-ink border border-line">Language: {a.language}</span>}
                    {a.interpreter_name && <span data-testid="appt-interpreter-badge" className="text-xs font-semibold px-2 py-0.5 rounded bg-clinical/10 text-clinical border border-clinical/30">Interpreter: {a.interpreter_name}</span>}
                    {a.interpreter_inactive && <span data-testid="appt-interpreter-inactive" className="text-xs font-bold px-2 py-0.5 rounded bg-critical/10 text-critical border border-critical/30 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Interpreter deactivated — reassign</span>}
                  </div>
                  <p className="text-sm text-muted2 mt-1">{a.patient_name} ({a.public_code || a.patient_username || "walk-in"}) · {fmtAppt(a)} · {a.duration} min</p>
                  <p className="text-[11px] text-muted2/80 mt-0.5">ID: {a.id}</p>
                  {a.participants?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="appt-participants">
                      {a.participants.map((p) => (
                        <span key={p.user_id} className="text-xs font-semibold bg-clinical/10 text-clinical rounded-full px-2 py-0.5">+ {p.name}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {a.status === "scheduled" && (
                  <>
                    {a.mode !== "walk_in" && (
                      <button onClick={() => onJoin(a.id)} data-testid="appt-join-btn" className="inline-flex items-center gap-1 bg-clinical text-white text-sm font-semibold rounded-md px-3 py-1.5"><Video className="w-4 h-4" /> Join</button>
                    )}
                    {a.mode !== "walk_in" && (
                      <button onClick={() => { const link = `${window.location.origin}/consult/${a.id}`; navigator.clipboard?.writeText(link).then(() => toast.success("Consultation link copied — send it to the patient to join directly.")).catch(() => toast.error("Could not copy link")); }} data-testid="appt-copy-link-btn" className="inline-flex items-center gap-1 border border-line text-clinical text-sm font-semibold rounded-md px-3 py-1.5"><Link2 className="w-4 h-4" /> Copy link</button>
                    )}
                    {a.mode !== "walk_in" && (
                      <button onClick={() => onWhatsapp(a)} data-testid="appt-whatsapp-btn" className="inline-flex items-center gap-1 bg-[#25D366] text-white text-sm font-semibold rounded-md px-3 py-1.5 hover:bg-[#1da851]"><MessageCircle className="w-4 h-4" /> WhatsApp</button>
                    )}
                    <button onClick={() => onReschedule(a)} data-testid="appt-reschedule-btn" className="inline-flex items-center gap-1 border border-clinical/40 text-clinical text-sm font-semibold rounded-md px-3 py-1.5"><CalendarClock className="w-4 h-4" /> Reschedule</button>
                    <button onClick={() => onRemind(a)} data-testid="appt-remind-btn" className="inline-flex items-center gap-1 border border-line text-clinical text-sm font-semibold rounded-md px-3 py-1.5"><Bell className="w-4 h-4" /> Remind</button>
                    <button onClick={() => onComplete(a)} data-testid="appt-complete-btn" className="inline-flex items-center gap-1 border border-calm text-calm text-sm font-semibold rounded-md px-3 py-1.5"><CheckCircle2 className="w-4 h-4" /> Complete</button>
                    <button onClick={() => onCancel(a)} data-testid="appt-cancel-btn" className="inline-flex items-center gap-1 text-critical text-sm font-semibold rounded-md px-2 py-1.5"><XCircle className="w-4 h-4" /></button>
                  </>
                )}
                {isSysAdmin && (
                  <button onClick={() => onRemove(a)} data-testid="appt-delete-btn" className="inline-flex items-center gap-1 border border-critical/30 text-critical text-sm font-semibold rounded-md px-3 py-1.5"><Trash2 className="w-4 h-4" /> Delete</button>
                )}
              </div>
              {a.status === "scheduled" && a.mode !== "walk_in" && (
                <InterpreterRowControl appt={a} onReassign={onReassignInterpreter} />
              )}
            </div>
          );
        })}
        {total === 0 && <p className="text-muted2">No appointments yet.</p>}
        {total > 0 && items.length === 0 && <p className="text-muted2" data-testid="appt-no-match">No appointments match your search.</p>}
      </div>
    </>
  );
}

export function AdminAppointments({ prefill }) {
  const [list, setList] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [avail, setAvail] = useState([]);
  const [selParts, setSelParts] = useState([]);
  const [selPatient, setSelPatient] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [requests, setRequests] = useState([]);
  const [join, setJoin] = useState(null);
  const { user } = useAuth();
  const isSysAdmin = user?.role === "system_admin";
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [rf, setRf] = useState({ date: "", time: "" });
  const [waModal, setWaModal] = useState(null);
  const openWa = (phone, msg) => {
    const clean = (phone || "").replace(/[^\d]/g, "");
    if (!clean) { toast.error("Enter a valid phone number including country code."); return; }
    window.open(`https://wa.me/${clean}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };
  const waMessage = (a) => {
    const link = `${window.location.origin}/consult/${a.id}`;
    return `Hello ${a.patient_name}, your ${a.mode} consultation with ${a.specialist_name} is on ${a.date} at ${a.time}. Tap this link to join directly: ${link}`;
  };
  const doWhatsapp = (a) => {
    const msg = waMessage(a);
    if (a.patient_phone) openWa(a.patient_phone, msg);
    else setWaModal({ appt: a, phone: "", msg });
  };
  const blank = { specialist_id: "", public_code: "", walkin_name: "", request_id: "", date: "", time: "", duration: 30, mode: "video", participant_ids: [], clinic_id: "virtual", language: "", interpreter_id: "" };
  const [form, setForm] = useState(blank);
  const [langOpts, setLangOpts] = useState([]);
  useEffect(() => { api.get("/languages").then(({ data }) => setLangOpts(data.map((l) => l.name))).catch(() => {}); }, []);
  const load = useCallback(async () => {
    try {
      const [a, s, av, cl, rq] = await Promise.all([api.get("/appointments"), api.get("/clinicians"), api.get("/admin/availability"), api.get("/clinics"), api.get("/admin/appointment-requests", { params: { status: "pending" } })]);
      setList(a.data); setSpecs(s.data); setAvail(av.data); setClinics(cl.data); setRequests(rq.data);
    } catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Prefill the scheduler when jumping here from an Appointment Request or the interpreter calendar.
  useEffect(() => {
    if (!prefill) return;
    setForm((f) => ({ ...blank, public_code: prefill.public_code || "", specialist_id: prefill.specialist_id || "", request_id: prefill.request_id || "", date: prefill.date || "" }));
    setSelParts([]);
    setSelPatient(prefill.public_code ? { value: prefill.public_code, label: prefill.public_code } : null);
    toast.info(prefill.date && !prefill.public_code ? `Booking for ${prefill.date} — pick the patient, clinician, time & interpreter.` : "Patient details carried over from the request — set the date & time to schedule.");
    window.scrollTo({ top: 0, behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?._ts]);

  const todayStr = new Date().toISOString().slice(0, 10);
  const [specialtyFilter, setSpecialtyFilter] = useState("all");
  const specialties = [...new Set(specs.map((s) => (s.specialty || "").trim()).filter(Boolean))].sort();
  const filteredSpecs = specialtyFilter === "all"
    ? specs
    : specs.filter((s) => (s.specialty || "").trim() === specialtyFilter);
  const specSlots = form.specialist_id
    ? avail.filter((s) => s.specialist_id === form.specialist_id && s.date >= todayStr)
        .sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time))
    : [];
  const toggleParticipant = (item) => setSelParts((cur) => cur.some((x) => x.id === item.id) ? cur.filter((x) => x.id !== item.id) : [...cur, item]);
  const patientAppts = form.public_code
    ? list.filter((a) => a.public_code === form.public_code && a.status === "scheduled" && a.date >= todayStr)
        .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time))
    : [];
  const toMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
  const overlaps = (aTime, aDur) => {
    const aS = toMin(aTime), aE = aS + (Number(aDur) || 0);
    const bS = toMin(form.time), bE = bS + (Number(form.duration) || 0);
    return aS < bE && bS < aE;
  };
  const clashAppt = form.public_code && form.date && form.time
    ? patientAppts.find((a) => a.date === form.date && overlaps(a.time, a.duration))
    : null;
  const clinicianClash = form.specialist_id && form.date && form.time
    ? list.find((a) => a.specialist_id === form.specialist_id && a.status === "scheduled" && a.date === form.date && overlaps(a.time, a.duration))
    : null;
  const anyClash = clashAppt || clinicianClash;
  const selClinician = specs.find((s) => s.id === form.specialist_id);
  const languageGap = form.mode !== "walk_in" && form.language && selClinician
    && !(selClinician.languages || []).includes(form.language) && !form.interpreter_id;
  const voiceReq = form.public_code
    ? requests.find((r) => r.patient_code === form.public_code && r.voice_note && r.status === "pending")
    : null;
  const clinicOptions = useMemo(() => [{ value: "virtual", label: "Virtual Consultation" }, ...clinics.filter((c) => c.active).map((c) => ({ value: c.id, label: c.name }))], [clinics]);
  const [clinicFilter, setClinicFilter] = useState("all");
  const clinicNameOf = (id) => clinics.find((c) => c.id === id)?.name || "";
  const filteredList = useMemo(() => {
    let out = clinicFilter === "all" ? list : list.filter((a) => a.clinic_id === clinicFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((a) => [a.patient_name, a.patient_username, a.public_code, a.id, a.date, a.specialist_name, clinicNameOf(a.clinic_id)]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, clinicFilter, search]);

  const create = async () => {
    const isWalkin = form.mode === "walk_in";
    const clinicId = form.clinic_id === "virtual" ? "" : form.clinic_id;
    if (isWalkin) {
      if (!clinicId) return toast.error("Select a physical clinic for the walk-in");
      if (!form.public_code && !form.walkin_name.trim()) return toast.error("Select a registered patient or enter a walk-in name");
      if (!form.date || !form.time) return toast.error("Set date & time");
    } else if (!form.specialist_id || !form.public_code || !form.date || !form.time) {
      return toast.error("Fill specialist, code, date & time");
    }
    if (form.date < todayStr || (form.date === todayStr && form.time < new Date().toTimeString().slice(0, 5))) {
      return toast.error("You cannot schedule an appointment in the past — pick the current time or later.");
    }
    if (anyClash) {
      const msgs = [];
      if (clashAppt) msgs.push(`This patient already has an appointment at ${clashAppt.date} ${clashAppt.time} with ${clashAppt.specialist_name}.`);
      if (clinicianClash) msgs.push(`${clinicianClash.specialist_name} is already booked at ${clinicianClash.date} ${clinicianClash.time} (${clinicianClash.patient_name}).`);
      if (!window.confirm(`${msgs.join("\n")}\n\nSchedule anyway?`)) return;
    }
    try { await api.post("/appointments", { ...form, clinic_id: clinicId, tz: localTz(), participant_ids: selParts.map((p) => p.id), duration: Number(form.duration), request_id: form.request_id || voiceReq?.id }); toast.success(isWalkin ? "Walk-in appointment booked" : "Appointment scheduled"); setForm(blank); setSelParts([]); setSelPatient(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const reassignInterpreter = async (aid, interpreterId) => {
    try {
      await api.put(`/appointments/${aid}/interpreter`, { interpreter_id: interpreterId || "" });
      toast.success(interpreterId ? "Interpreter assigned" : "Interpreter removed");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const complete = async (a) => {
    if (!window.confirm("Confirm the consultation is complete and that patient notes were recorded in the authorised EXTERNAL clinical system (not here)?")) return;
    try { await api.put(`/appointments/${a.id}/complete`, { notes_confirmed: true }); toast.success("Marked completed"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const cancel = async (a) => { try { await api.put(`/appointments/${a.id}/cancel`); toast.success("Cancelled"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const remind = async (a) => { try { const { data } = await api.post(`/appointments/${a.id}/remind`); toast.success(`Reminder sent to ${data.sent} device(s)`); } catch (e) { toast.error(apiErr(e)); } };
  const remove = async (a) => { if (!window.confirm("Permanently delete this appointment record?")) return; try { await api.delete(`/appointments/${a.id}`); toast.success("Appointment deleted"); setSelected((s) => s.filter((x) => x !== a.id)); load(); } catch (e) { toast.error(apiErr(e)); } };
  const openReschedule = (a) => { setRescheduleAppt(a); setRf({ date: a.date, time: a.time }); };
  const doReschedule = async () => {
    if (!rf.date || !rf.time) return toast.error("Pick a new date & time");
    try {
      await api.put(`/appointments/${rescheduleAppt.id}/reschedule`, { date: rf.date, time: rf.time, tz: localTz() });
      toast.success("Appointment rescheduled — the patient has been notified.");
      setRescheduleAppt(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const bulkDelete = async () => {
    if (!window.confirm(`Permanently delete ${selected.length} appointment(s)? This cannot be undone.`)) return;
    try { const { data } = await api.post("/appointments/bulk-delete", { ids: selected }); toast.success(`Deleted ${data.deleted} appointment(s)`); setSelected([]); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <h1 className="font-heading font-bold text-3xl tracking-tight text-ink mb-6 flex items-center gap-2"><CalendarPlus className="w-7 h-7 text-clinical" /> Virtual Appointments</h1>
      <div className="bg-white border border-line rounded-md p-4 mb-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="appointment-form">
        <select className={inp} value={specialtyFilter} onChange={(e) => { setSpecialtyFilter(e.target.value); setForm({ ...form, specialist_id: "" }); }} data-testid="appt-specialty-filter">
          <option value="all">All specialties</option>
          {specialties.map((sp) => <option key={sp} value={sp}>{sp}</option>)}
        </select>
        <select className={inp} value={form.specialist_id} onChange={(e) => setForm({ ...form, specialist_id: e.target.value })} data-testid="appt-specialist">
          <option value="">— Choose clinician —</option>
          {filteredSpecs.map((s) => <option key={s.id} value={s.id}>{s.title} {s.name}{s.specialty ? ` — ${s.specialty}` : ""}</option>)}
        </select>
        <SearchableSelect
          testid="appt-patient"
          value={form.public_code}
          selectedOption={selPatient}
          onSearch={async (query) => {
            const { data } = await api.get("/patients", { params: { q: query, limit: 20 } });
            return data.map((p) => ({ value: p.code, label: p.code, sublabel: p.name }));
          }}
          onChange={(v, opt) => { setForm({ ...form, public_code: v }); setSelPatient(opt || null); }}
          placeholder="Select patient (code or name)"
          searchPlaceholder="Search code or patient name…"
          clearable
        />
        <select className={inp} value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="appt-mode">
          <option value="video">Video</option><option value="audio">Audio</option><option value="text">Text chat</option>
          <option value="walk_in">Walk-in (in-person)</option>
        </select>
        {form.mode === "walk_in" && (
          <input className={inp} value={form.walkin_name} onChange={(e) => setForm({ ...form, walkin_name: e.target.value })} placeholder="Walk-in name (if not a registered patient)" data-testid="appt-walkin-name" />
        )}
        <SearchableSelect
          testid="appt-clinic"
          options={clinicOptions}
          value={form.clinic_id}
          onChange={(v) => setForm({ ...form, clinic_id: v })}
          placeholder={form.mode === "walk_in" ? "Clinic location (required)" : "Clinic location (optional)"}
          searchPlaceholder="Search clinics…"
          clearable
        />
        <input type="date" min={todayStr} className={inp} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="appt-date" />
        <input type="time" min={form.date === todayStr ? new Date().toTimeString().slice(0, 5) : undefined} className={inp} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="appt-time" />
        <input type="number" className={inp} value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="Minutes" />
        <select className={inp} value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} data-testid="appt-language">
          <option value="">Preferred language (optional)</option>
          {langOpts.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <button onClick={create} data-testid="appt-create-btn" className={`inline-flex items-center justify-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold lg:col-span-3 transition-colors ${anyClash ? "bg-alert text-white hover:bg-alert/90" : "bg-clinical text-white hover:bg-clinicaldark"}`}>
          {anyClash
            ? <><AlertTriangle className="w-4 h-4" /> {clinicianClash && clashAppt ? "Patient & clinician clash" : clinicianClash ? "Clinician double-booked" : "Patient double-booking"} — schedule anyway?</>
            : <><Plus className="w-4 h-4" /> Schedule appointment</>}
        </button>
        {voiceReq && (
          <div className="lg:col-span-3 flex items-center gap-3 bg-clinical/5 border border-clinical/20 rounded-md px-3 py-2" data-testid="appt-voicenote-note">
            <Mic className="w-4 h-4 text-clinical shrink-0" />
            <span className="text-xs text-ink">This patient attached a voice note to their request. It will be linked to this appointment and <b>kept with the request</b> for reference.</span>
            <audio controls controlsList="nodownload noplaybackrate" onContextMenu={(e) => e.preventDefault()} src={abs(voiceReq.voice_note)} className="h-8 ml-auto" data-testid="appt-voicenote-audio" />
          </div>
        )}
        {form.specialist_id && (
          <AvailabilityPicker slots={specSlots} date={form.date} time={form.time} onPick={(date, time) => setForm({ ...form, date, time })} />
        )}
        {languageGap && (
          <div className="lg:col-span-3 flex items-start gap-2 bg-alert/10 border border-alert/30 rounded-md px-3 py-2" data-testid="language-gap-warning">
            <AlertTriangle className="w-4 h-4 text-alert shrink-0 mt-0.5" />
            <span className="text-xs text-ink">{selClinician?.name || "The chosen clinician"} does not list <b>{form.language}</b> as a language they speak, and no interpreter is assigned. Consider assigning an interpreter below.</span>
          </div>
        )}
        {form.mode !== "walk_in" && (
          <InterpreterPicker value={form.interpreter_id} onChange={(v) => setForm({ ...form, interpreter_id: v })}
            language={form.language} date={form.date} time={form.time} duration={form.duration} />
        )}
        <ParticipantPicker selectedItems={selParts} onToggle={toggleParticipant} />
        {form.public_code && (
          <div className="lg:col-span-3" data-testid="patient-upcoming">
            <p className="text-xs uppercase tracking-wider font-bold text-muted2 mb-2">
              This patient's upcoming appointments {patientAppts.length > 0 && <span className="text-clinical">({patientAppts.length})</span>}
            </p>
            {patientAppts.length === 0 ? (
              <p className="text-sm text-muted2">No upcoming appointments — good to schedule.</p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {patientAppts.map((a) => (
                  <div key={a.id} data-testid="patient-upcoming-item" className="flex items-center gap-2 text-sm bg-sand border border-line rounded-md px-3 py-1.5">
                    <CalendarPlus className="w-4 h-4 text-clinical shrink-0" />
                    <span className="font-semibold text-ink">{fmtAppt(a)}</span>
                    <span className="text-muted2">· {a.specialist_name} · {a.duration} min · {a.mode}</span>
                  </div>
                ))}
                <p className="text-xs text-alert font-medium mt-0.5">Check for double-booking before scheduling another.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <AppointmentList
        items={filteredList}
        total={list.length}
        clinics={clinics}
        clinicFilter={clinicFilter}
        onClinicFilter={setClinicFilter}
        search={search}
        onSearch={setSearch}
        onJoin={setJoin}
        onRemind={remind}
        onComplete={complete}
        onCancel={cancel}
        onRemove={remove}
        onReschedule={openReschedule}
        onWhatsapp={doWhatsapp}
        onReassignInterpreter={reassignInterpreter}
        isSysAdmin={isSysAdmin}
        selected={selected}
        onToggleSelect={toggleSelect}
        onBulkDelete={bulkDelete}
      />

      {rescheduleAppt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="reschedule-modal" onClick={() => setRescheduleAppt(null)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-bold text-xl text-ink flex items-center gap-2 mb-1"><CalendarClock className="w-5 h-5 text-clinical" /> Reschedule appointment</h3>
            <p className="text-sm text-muted2 mb-4">{rescheduleAppt.patient_name} with {rescheduleAppt.specialist_name}. The patient will be notified of the new time.</p>
            <div className="grid grid-cols-2 gap-3">
              <input type="date" min={todayStr} className={inp} value={rf.date} onChange={(e) => setRf({ ...rf, date: e.target.value })} data-testid="reschedule-date" />
              <input type="time" className={inp} value={rf.time} onChange={(e) => setRf({ ...rf, time: e.target.value })} data-testid="reschedule-time" />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setRescheduleAppt(null)} className="px-4 py-2 text-sm font-semibold text-muted2 hover:text-ink">Cancel</button>
              <button onClick={doReschedule} data-testid="reschedule-confirm-btn" className="inline-flex items-center gap-1.5 bg-clinical text-white rounded-md px-4 py-2 text-sm font-semibold"><Check className="w-4 h-4" /> Reschedule & notify</button>
            </div>
          </div>
        </div>
      )}

      {waModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="whatsapp-modal" onClick={() => setWaModal(null)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-bold text-xl text-ink flex items-center gap-2 mb-1"><MessageCircle className="w-5 h-5 text-[#128C7E]" /> Send link on WhatsApp</h3>
            <p className="text-sm text-muted2 mb-4">{waModal.appt.patient_name} has no phone number on file. Enter the patient's WhatsApp number (with country code) to open a pre-filled message.</p>
            <input
              data-testid="whatsapp-phone-input"
              className={inp}
              placeholder="+263 77 123 4567"
              value={waModal.phone}
              onChange={(e) => setWaModal({ ...waModal, phone: e.target.value })}
            />
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setWaModal(null)} className="px-4 py-2 text-sm font-semibold text-muted2 hover:text-ink">Cancel</button>
              <button onClick={() => { openWa(waModal.phone, waModal.msg); setWaModal(null); }} data-testid="whatsapp-send-btn" className="inline-flex items-center gap-1.5 bg-[#25D366] text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-[#1da851]"><MessageCircle className="w-4 h-4" /> Open WhatsApp</button>
            </div>
          </div>
        </div>
      )}

      {join && <JitsiRoom appointmentId={join} onClose={() => { setJoin(null); load(); }} />}
    </div>
  );
}
